import { useMemo } from 'react'
import { formatUsd } from '@components/Web3/useEthPrice'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { ETH_ADDRESS } from '@components/Web3/utils'
// eslint-disable-next-line no-restricted-imports
import { BigNumber } from 'ethers'
import { combineEthAndUsdcInUsd } from 'hooks/useFormattedNormalizedTipAmount'
import { useMembershipInfo, useMembershipInfoWithPricingModule } from 'use-towns-client'
import { Address, formatUnits } from 'viem'

export function useReadableMembershipInfo(
    networkId: string,
    {
        showEthInUsd = false,
        ethPrice = undefined,
    }: {
        showEthInUsd?: boolean
        ethPrice?: string
    } = {},
) {
    const response = useMembershipInfoWithPricingModule(networkId)
    const { address: usdcAddress } = useUSDCMetadata()

    return useMemo(() => {
        return {
            ...response,
            data: transformData({
                membershipInfo: response.data,
                usdcAddress,
                showEthInUsd,
                ethPrice,
            }),
        }
    }, [response, usdcAddress, showEthInUsd, ethPrice])
}

function transformData(args: {
    membershipInfo: ReturnType<typeof useMembershipInfo>['data']
    usdcAddress: Address
    showEthInUsd?: boolean
    ethPrice?: string
}):
    | {
          price: string | undefined
          maxSupply: number
          currency: 'USDC' | 'ETH'
          totalSupply: number
          duration: number
          remainingFreeSupply: number
          feeRecipient: string
          pricingModule: string
      }
    | undefined {
    const { membershipInfo, usdcAddress, showEthInUsd, ethPrice } = args
    if (!membershipInfo) {
        return
    }

    const currency = membershipInfo.currency

    return {
        ...membershipInfo,
        price: transformPrice({
            price: BigNumber.from(membershipInfo.price).toBigInt(),
            currency,
            usdcAddress,
            showEthInUsd,
            ethPrice,
        }),
        maxSupply: Number(membershipInfo.maxSupply),
        currency: transformCurrency(currency, usdcAddress),
        totalSupply: membershipInfo.totalSupply,
        duration: Number(membershipInfo.duration),
        remainingFreeSupply: membershipInfo.remainingFreeSupply,
    }
}

interface TransformPriceArgs {
    price: bigint
    currency: string
    usdcAddress: Address
    showEthInUsd?: boolean
    ethPrice?: string
}

function transformPrice(args: TransformPriceArgs) {
    const { price, currency, usdcAddress, showEthInUsd, ethPrice } = args
    if (currency.toLowerCase() === ETH_ADDRESS.toLowerCase()) {
        const eth = formatUnits(price, 18)

        switch (eth) {
            case '0.0':
                return 'Free'
            default:
                if (showEthInUsd && ethPrice) {
                    return combineEthAndUsdcInUsd({
                        ethAmount: price,
                        usdcAmount: 0n,
                        ethPrice,
                    })
                }
                return eth
        }
    } else if (currency.toLowerCase() === usdcAddress.toLowerCase()) {
        // this should work for most spots?
        const usdc = formatUnits(price, 6)
        return formatUsd(usdc)
    }
}

function transformCurrency(currency: string, usdcAddress: Address) {
    switch (currency.toLowerCase()) {
        case usdcAddress.toLowerCase():
            return 'USDC'
        case ETH_ADDRESS.toLowerCase():
        default:
            return 'ETH'
    }
}
